import colors from '@/utils/colors';
import { chain } from 'lodash';

const {
  positiveColor,
  neutralColor,
  negativeColor,
  notClassifiedColor,
  defaultSerieColor,
} = colors;

const SENTIMENT_LABEL = {
  POSITIVE: 'Positivo',
  NEUTRAL: 'Neutro',
  NEGATIVE: 'Negativo',
  NOT_CLASSIFIED: 'Não Classificado',
};

const SENTIMENT_MAP = {
  3: SENTIMENT_LABEL.NEUTRAL,
  2: SENTIMENT_LABEL.NEGATIVE,
  1: SENTIMENT_LABEL.POSITIVE,
  '-2': SENTIMENT_LABEL.NOT_CLASSIFIED,
  aliases: {
    0: SENTIMENT_LABEL.NEUTRAL,
    '-1': SENTIMENT_LABEL.NEGATIVE,
  },
};

const getSentiment = (value) => {
  return SENTIMENT_MAP[value] || SENTIMENT_MAP.aliases[value] || value;
};

const isNegativeLabel = (label) => {
  return [
    SENTIMENT_LABEL.NEGATIVE,
    'negative',
    'negative_value',
  ].includes(label);
};

const isNeutralLabel = (label) => {
  return [
    SENTIMENT_LABEL.NEUTRAL,
    'neutral',
    'neutral_value',
  ].includes(label);
};

const isPositiveLabel = (label) => {
  return [
    SENTIMENT_LABEL.POSITIVE,
    'positive',
    'positive_value',
  ].includes(label);
};

const isNotClassifiedLabel = (label) => {
  return [
    SENTIMENT_LABEL.NOT_CLASSIFIED,
    'notclassified',
    'not_classified_value',
  ].includes(label);
};

const mapIndexFromDimensions = (points) => {
  const mapped = points.reduce((map, point) => {
    if (!map[point.x]) {
      map[point.x] = 0;
    }
    map[point.x] += point.z;
    return map;
  }, {});
  return chain(mapped).toPairs().sortBy(1)
    .reverse().flatMap(d => d[0]).value();
};

export default {
  data() {
    return {
      positiveColor,
      neutralColor,
      negativeColor,
      notClassifiedColor,
      sentiment: [
        'positive',
        'neutral',
        'negative',
      ],
      sentimentList: SENTIMENT_MAP,
    };
  },
  methods: {
    normalizeSentimentFromPoints(points = [], hideNull = false) {
      // prepare data normalizing name and ordering dimensions
      const normalizedPoints = points.filter(p => {
        if (this.isSentimentSerie(p.x)) {
          const x = p.y;
          p.y = p.x;
          p.x = x;
        }
        if (hideNull) {
          return p.x;
        }
        p.x = p.x || 'Indefinido';
        return p;
      });
      const mappedSeries = {};
      // creates a static index ordered by total posts
      const indexes = mapIndexFromDimensions(normalizedPoints);
      const sumPoints = normalizedPoints.reduce((total, point) => total + point.z, 0);
      normalizedPoints.forEach(point => {
        // if not mapped yet, creates a new serie
        if (!mappedSeries[point.y]) {
          const name = this.sentimenValueToLabel(point.y);
          mappedSeries[point.y] = {
            name,
            color: this.getSentimentColor(name),
            order: point.y,
            data: [],
          };
        }
        // and just add the new data after creation or when already created before
        mappedSeries[point.y].data.push({
          name: point.x,
          x: indexes.indexOf(point.x),
          y: point.z,
          percentY: (point.z / sumPoints) * 100,
        });
      });
      // grants that sentiment series comes out always in the same order
      return Object.values(mappedSeries)
        .sort((a, b) => Number(a.order) < Number(b.order) ? 1 : -1);
    },
    normalizeSentimentFromSeries(response) {
      for (const [, value] of Object.entries(response)) {
        if (!Array.isArray(value)) {
          continue;
        }
        value.forEach(prop => {
          if (!prop instanceof Object) {
            return;
          }

          if (prop.label === 'polarity' && Array.isArray(prop.values)) {
            prop.values = prop.values.map(value => this.sentimenValueToLabel(value));
          }

          if (this.isSentimentSerie(prop.name)) {
            prop.name = this.sentimenValueToLabel(prop.name);
          }
        });
      }
      return response;
    },
    normalizeSentimentResponseFromChartService(response, pointsParser = 'series', hideNull) {
      switch (pointsParser) {
      case 'points':
        return this.normalizeSentimentFromPoints(response, hideNull);
      default:
        return this.normalizeSentimentFromSeries(response, hideNull);
      }
    },
    getSentimentObj(title) {
      return {
        color: this.getSentimentColor(title),
        name: this.getSentimentLabel(title),
        value: this.getSentimentValue(title),
      };
    },
    sentimentSeriesToScore(response) {
      const normalizedResponse = this.normalizeSentimentResponseFromChartService(response);
      const series = normalizedResponse.points;

      if (!series || !series.length) {
        return 0;
      }

      const totalPosts = series.reduce((total, serie) => total + serie.data[0], 0);

      if (totalPosts === 0) {
        return 0;
      }

      const { Positivo = 0, Negativo = 0 } = series.reduce((map, sent) => {
        map[sent.name] = sent.data[0] || 0;
        return map;
      }, {});

      return ((Positivo - Negativo) / totalPosts) * 100;
    },
    isSentimentSerie(name) {
      return [-2, -1, 0, 1].includes(Number(name));
    },
    sentimenValueToLabel(val) {
      return getSentiment(val);
    },
    getSentimentColor(title) {
      if (isNegativeLabel(title)) {
        return this.negativeColor;
      }

      if (isNeutralLabel(title)) {
        return this.neutralColor;
      }

      if (isPositiveLabel(title)) {
        return this.positiveColor;
      }

      if (isNotClassifiedLabel(title)) {
        return this.notClassifiedColor;
      }

      return defaultSerieColor;
    },
    getSentimentValue(title) {
      if (isNegativeLabel(title)) {
        return 2;
      }

      if (isNeutralLabel(title)) {
        return 3;
      }

      if (isPositiveLabel(title)) {
        return 1;
      }
    },
    getSentimentLabel(title) {
      if (isNegativeLabel(title)) {
        return SENTIMENT_LABEL.NEGATIVE;
      }

      if (isNeutralLabel(title)) {
        return SENTIMENT_LABEL.NEUTRAL;
      }

      if (isPositiveLabel(title)) {
        return SENTIMENT_LABEL.POSITIVE;
      }

      if (isNotClassifiedLabel(title)) {
        return SENTIMENT_LABEL.NOT_CLASSIFIED;
      }

      return title;
    },
  },
};
